import { useState, useEffect } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  addDays,
  format,
} from "date-fns";
import useApi from "./useApi";

const useSupervisorCalendarData = (
  currentMonth,
  filters,
  supervisorId = null,
) => {
  const [calendarData, setCalendarData] = useState({});
  const [loadingWeeks, setLoadingWeeks] = useState({});
  const { postData } = useApi();

  // Generate up to 6 weeks for the month
  const getWeeksInMonth = (month) => {
    const weeks = [];
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);

    let currentWeekStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    let weekNum = 0;

    while (currentWeekStart <= monthEnd && weekNum < 6) {
      const currentWeekEnd = addDays(currentWeekStart, 6);

      weeks.push({
        weekNum,
        startDate: format(currentWeekStart, "yyyy-MM-dd"),
        endDate: format(currentWeekEnd, "yyyy-MM-dd"),
      });

      currentWeekStart = addDays(currentWeekStart, 7);
      weekNum++;
    }

    return weeks;
  };

  const fetchWeekData = async (week, currentFilters) => {
    setLoadingWeeks((prev) => ({ ...prev, [week.weekNum]: true }));

    try {
      const result = await postData({
        entityName: "Call",
        action: "getSupervisorCalendarData",
        data: {
          startDate: week.startDate,
          endDate: week.endDate,
          repId: currentFilters.repId === "all" ? null : currentFilters.repId,
          clientId:
            currentFilters.clientId === "all" ? null : currentFilters.clientId,
          projectId:
            currentFilters.projectId === "all"
              ? null
              : currentFilters.projectId,
          status:
            currentFilters.status === "all" ? null : currentFilters.status,
          ...(supervisorId && { supervisorId }),
        },
      });

      if (result?.isSuccessful) {
        setCalendarData((prev) => ({
          ...prev,
          ...result.data.calendarData,
        }));
      }
    } catch (error) {
      console.error("Error loading week data:", error);
    } finally {
      setLoadingWeeks((prev) => ({ ...prev, [week.weekNum]: false }));
    }
  };

  // Load all visible weeks sequentially on mount or filter change
  useEffect(() => {
    setCalendarData({});
    setLoadingWeeks({});

    const weeks = getWeeksInMonth(currentMonth);

    // Sequential loading: load each week one after another
    const loadWeeks = async () => {
      for (const week of weeks) {
        await fetchWeekData(week, filters);
      }
    };

    loadWeeks();
  }, [currentMonth, JSON.stringify(filters), supervisorId]);

  return { calendarData, loadingWeeks };
};

export default useSupervisorCalendarData;
